/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import {
  DropAreaWorkflowGraphNode,
  ActionWorkflowGraphNode,
  WorkflowGraph,
  WorkflowGraphNode,
} from 'cio/utils/visual-workflow-builder/workflow-graph';
import CampaignModel, { Edge, ParsedTreeBranchNodeBranch } from 'cio/models/campaign';
import ActionModel, { BRANCH_TYPES } from 'cio/models/action';
import { TaskGenerator } from 'ember-concurrency';
import { isNone } from '@ember/utils';
import { edgesEndingInExitCount } from 'cio/utils/visual-workflow-builder/branching';
import { action, computed, setProperties } from '@ember/object';
import { inject as service } from '@ember/service';
import { DEFAULT_SIZE_SHORT } from 'cio/utils/visual-workflow-builder/layout/size';
import { tracked } from '@glimmer/tracking';
import { DragMode, DropTargetItem, DropType } from 'cio/utils/visual-workflow-builder/types';
import VisualWorkflowBuilderService from 'cio/services/visual-workflow-builder';
import { htmlSafe } from '@ember/template';
import { FlyDragEvent } from 'fly-drag-drop/utils/events';
import FlyDragDropManager from 'fly-drag-drop/services/fly-drag-drop-manager';

interface VisualWorkflowBuilderV2NodeDropArgs {
  workflowGraph: WorkflowGraph;
  node: DropAreaWorkflowGraphNode;
  container: HTMLDivElement;
  campaign: CampaignModel;
  dragMode: DragMode;
  highlightedNodeIds: string[];
  addActionTask: TaskGenerator<any>;
  selectedAction: ActionModel;
  isSelectingDropAreaThroughClick: boolean;
  isCopyingHighlightedActions: boolean;
  isMovingHighlightedActions: boolean;
  onCopyHighlightedActions: (dropTargetItem: DropTargetItem, dropType: DropType) => unknown;
  onMoveHighlightedActions: (dropTargetItem: DropTargetItem, dropType: DropType) => unknown;
  onCancelDragOperation: (isPerformingDragOperation: boolean) => unknown;
  onDropOrClickDropArea: (dropTargetItem: DropTargetItem, dropType: DropType, opts: any) => unknown;
  dragStartEvent: FlyDragEvent | null;
  trackInViewport: boolean;
  hasReadOnlyPermission: boolean;
}

const TRANSLATE_FORCE = 2500;

const FORCE_THRESHOLD_TOP = 200;
const FORCE_THRESHOLD_X = 500;
const FORCE_THRESHOLD_BOTTOM = 500;

// We subtract 2 for borders
const MAX_TRIGGER_OFFSET = DEFAULT_SIZE_SHORT.height - 2;
const MAX_BRANCH_DOWN_OFFSET = 10;

export default class VisualWorkflowBuilderV2NodeDrop extends Component<VisualWorkflowBuilderV2NodeDropArgs> {
  @service flyDragDropManager!: FlyDragDropManager;
  @service visualWorkflowBuilder!: VisualWorkflowBuilderService;

  @tracked canDrop = false;

  get beforeAfterClass() {
    let classes = [];

    if (this.args.node.source?.type === 'virtual_path_label') {
      classes.push('is-after-path-label');
    }

    if (this.args.node.target?.type === 'exit_action') {
      classes.push('is-before-exit');
    }

    return classes.join(' ');
  }

  @computed('args.node.{x,y}')
  get plusIconStyle() {
    return htmlSafe(`
--nodeXCoord: ${this.args.node.x}px;
--nodeYCoord: ${this.args.node.y}px;
`);
  }

  @action
  resetTranslatedNodes() {
    (this.closeNodes ?? []).forEach((node) => {
      setProperties(node, {
        translateX: 0,
        translateY: 0,
      });
    });
  }

  @action
  onDragLeave() {
    this.resetTranslatedNodes();
  }

  get closeNodes() {
    let dropNode = this.args.node;

    let visibleNodes = Array.from(this.args.container.querySelectorAll('[data-node-in-viewport="true"]')).reduce<
      WorkflowGraphNode[]
    >((acc, e: HTMLElement) => {
      let id = e.dataset.nodeId as string;
      if (id == null) {
        return acc;
      }

      return [...acc, this.args.workflowGraph.nodes[id]];
    }, []);

    let closeNodes = visibleNodes.filter((node) => {
      if (node.typeCategory === 'virtual' && node.type !== 'virtual_enter') {
        return false;
      }

      let isWithinXThreshold = Math.abs(node.x - dropNode.x) <= FORCE_THRESHOLD_X;

      if (!isWithinXThreshold) {
        return false;
      }

      let isWithinTopThreshold = node.y <= dropNode.y && Math.abs(node.y - dropNode.y) <= FORCE_THRESHOLD_TOP;
      let isWithinBottomThreshold = node.y >= dropNode.y && Math.abs(node.y - dropNode.y) <= FORCE_THRESHOLD_BOTTOM;

      return isWithinTopThreshold || isWithinBottomThreshold;
    });

    return closeNodes;
  }

  @action
  onDragOver() {
    let dropNode = this.args.node;
    let ignoreNode: string | null = null;

    if (
      (this.flyDragDropManager as any).currentDraggable?.action?.id &&
      !(this.flyDragDropManager as any).currentDraggable?.copy
    ) {
      ignoreNode = (this.flyDragDropManager as any).currentDraggable.action.id;
    }

    (this.closeNodes ?? []).forEach((node) => {
      if (ignoreNode && node.id === ignoreNode) {
        return;
      }

      let vector = {
        x: Math.floor(node.x - dropNode.x),
        y: Math.floor(node.y - dropNode.y),
      };
      let distanceSquared = Math.pow(vector.x, 2) + Math.pow(vector.y, 2);
      let scaledVector = {
        x: Math.floor((vector.x * TRANSLATE_FORCE) / distanceSquared),
        y: Math.floor((vector.y * TRANSLATE_FORCE) / distanceSquared),
      };

      // If the node is a "Trigger" node, don't move it upwards more than its
      // height, since there's no path line extending further above it and we'd
      // end up with a gap
      if (node.type === 'filter_match_delay_action' || node.type === 'virtual_enter') {
        scaledVector.y = Math.max(scaledVector.y, -MAX_TRIGGER_OFFSET);
      }

      // If the node is an exit node, don't move it down, as there's no path
      // line extending underneath it
      if (node.type === 'exit_action') {
        scaledVector.y = 0;
      }

      if (BRANCH_TYPES.includes(node.type)) {
        scaledVector.y = Math.min(scaledVector.y, MAX_BRANCH_DOWN_OFFSET);
      }

      setProperties(node, {
        translateX: scaledVector.x,
        translateY: scaledVector.y,
      });
    });
  }

  get isLastDropAreaInBranchPath() {
    if (!this.branch) {
      return false;
    }

    if (this.onDropType === 'insertAfter' || this.onDropType === 'insertAtBranchEnd') {
      let currentBranchPath = this.branch.edges[this.pathIndex];

      return (
        currentBranchPath.nodes.findIndex((edgeNode) => edgeNode.id === this.onDropItem?.id) ===
        currentBranchPath.nodes.length - 1
      );
    }

    return false;
  }

  // This is only used to make testing a little easier with saner IDs for
  // targeting elements
  get testItemId() {
    if (this.onDropType === 'insertAtBranchEnd') {
      return this.args.node?.source.branch?.id;
    }

    if (this.onDropType === 'insertAtEdge') {
      return this.args.node.branchId;
    }

    if (this.onDropType === 'insertAfter') {
      return this.args.node.source.id;
    }

    if (this.onDropType === 'insertBefore') {
      return this.args.node.target.id;
    }

    return null;
  }

  get onDropItem() {
    if (this.onDropType === 'insertAtBranchEnd') {
      return this.args.node?.source.branch;
    }

    if (this.onDropType === 'insertAtEdge') {
      let edge = { ...this.args.node.edge } as any;
      delete edge.id;
      return edge;
    }

    if (this.onDropType === 'insertAfter') {
      return {
        id: this.args.node.source.id,
        action: (this.args.node.source as ActionWorkflowGraphNode).action,
        type: 'action',
      };
    }

    if (this.onDropType === 'insertBefore') {
      return {
        id: this.args.node.target.id,
        action: (this.args.node.target as ActionWorkflowGraphNode).action,
        type: 'action',
      };
    }

    return null;
  }

  get onDropType() {
    if (this.args.node?.source?.type === 'virtual_rejoin') {
      return 'insertAtBranchEnd';
    }

    if (this.args.node?.edge?.type === 'branch' && this.args.node?.target?.type === 'virtual_rejoin') {
      return 'insertAtEdge';
    }

    if (this.args.node?.source.typeCategory === 'action' && !this.args.node.source.action.isExit) {
      return 'insertAfter';
    }

    if (this.args.node?.target.typeCategory === 'action') {
      return 'insertBefore';
    }

    return '';
  }

  get parentActions() {
    let immediateBranchId = this.args.node?.branchId;

    if (!immediateBranchId) {
      return [];
    }

    return [immediateBranchId, ...(this.args.campaign.parentBranchChains[immediateBranchId] ?? [])];
  }

  get reconnectForBranchId() {
    if (this.args.node.source?.type === 'virtual_rejoin') {
      return this.args.node.branchId;
    }

    return null;
  }

  // This is used to guard against the special case of the Wait Until action
  // only having one outgoing edge.
  // A wait until action is actually a branch action, but if there are no
  // conditions (or there is only one condition), it only has one outgoing path.
  // In these cases, we want the "branch" and "pathIndex" to refer to the parent
  // branch, if it exists - rather than the wait until branch - to make sure any
  // "canDrop" logic is calculated correctly.
  get isDropNodeForSinglePathBranchAction() {
    if (this.args.node.branch == null) {
      return false;
    }

    return (this.args.node.branch as any).edges.length === 1;
  }

  get branch(): ParsedTreeBranchNodeBranch | null {
    // If this drop node lives right after a wait until action with only one
    // path, use the parent branch (if any) instead of the wait until action
    // itself
    if (this.isDropNodeForSinglePathBranchAction) {
      if (this.args.node.branchId == null) {
        return null;
      }

      let parentBranchIdForWaitUntil = this.args.campaign.parentBranchMap[this.args.node.branchId];
      return this.args.campaign.branchesMap?.[parentBranchIdForWaitUntil];
    }

    if (this.args.node?.edge?.type === 'branch') {
      return this.args.node.branch as ParsedTreeBranchNodeBranch;
    }

    if (this.args.node.source?.type === 'virtual_rejoin') {
      let branchIdOfRejoin = this.args.node.branchId;
      if (!branchIdOfRejoin) {
        return null;
      }
      let parentBranchId = this.args.campaign.parentBranchMap[branchIdOfRejoin];

      if (!parentBranchId) {
        return null;
      }

      return this.args.campaign.branchesMap?.[parentBranchId];
    }

    let immediateBranchId = this.args.node?.branchId;

    if (!immediateBranchId) {
      return null;
    }

    return this.args.campaign.branchesMap?.[immediateBranchId];
  }

  get pathIndex() {
    // If we're under a wait until action with only one outgoing path, ignore
    // the edge index (it refers to the wait until action's edges), and continue
    // to find the index from the parent branch (if any).
    if (this.args.node?.edge?.type === 'branch' && !this.isDropNodeForSinglePathBranchAction) {
      return this.args.node.edge.index;
    }

    if (this.args.node.source?.type === 'virtual_rejoin') {
      let branchIdOfRejoin = this.args.node.branchId;
      if (isNone(this.branch) || branchIdOfRejoin == null) {
        return null;
      }

      let edge = this.branch.edges.find((e) => e.nodes.mapBy('id').includes(branchIdOfRejoin!));
      return edge?.edge.index;
    }

    let { branch } = this;

    if (!branch) {
      return null;
    }

    let edge = (branch as any).edges.find((e: any) => e.nodes.mapBy('id').includes(this.args.node.source.id));

    return edge.edge.index;
  }

  get allBranchesEndInExits() {
    if (!this.branch) {
      return false;
    }

    return this.branchesEndingInExitsCount === this.branch.edges.length;
  }

  get branchesEndingInExitsCount() {
    if (!this.branch) {
      return 0;
    }

    return edgesEndingInExitCount(this.branch);
  }

  get isValidReconnectPointForExit() {
    if (!this.args.selectedAction?.isExit) {
      return false;
    }

    let exitMeta: any = this.visualWorkflowBuilder.exitToDeleteMeta;

    let branchAction = this.args.campaign.workflowActions.findBy('id', exitMeta.branchId);

    let isSameBranch = exitMeta.branchId === this.branch?.id;
    let isDifferentPath = exitMeta.pathIndex !== this.pathIndex;

    if (branchAction) {
      if (this.allBranchesEndInExits) {
        return isSameBranch && isDifferentPath;
      }

      let { branch } = exitMeta;
      let otherPaths = branch.edges.filter((_edge: Edge, index: number) => {
        return index !== this.pathIndex && index !== exitMeta.pathIndex;
      });

      let doOtherPathsOnlyHaveExits = otherPaths.every((path: any) => {
        return path.nodes.length === 1 && path.nodes[0].action.type === 'exit_action';
      });

      if (this.onDropType === 'insertBefore') {
        // insertBefore drop points only appear in a path when there is at
        // least one action in it (when there is only one action and it's an
        // exit, that also gets an insertBefore).
        // In those cases, it will be the first drop point available.
        // If there is at least one path in the split that has other actions
        // in it, we can't reconnect to this insertBefore point, because that
        // would lead to an illegal state.
        // See https://github.com/customerio/issues/issues/3600
        return isSameBranch && isDifferentPath && doOtherPathsOnlyHaveExits;
      }

      if (this.onDropType === 'insertAfter' || this.onDropType === 'insertAtBranchEnd') {
        if (doOtherPathsOnlyHaveExits) {
          return isSameBranch && isDifferentPath;
        }

        // If other paths have non-exit actions inside them, the we can only
        // reconnect to the last drop point in this path.
        return isSameBranch && isDifferentPath && this.isLastDropAreaInBranchPath;
      }

      if (
        this.args.selectedAction?.id &&
        !isNone(this.args.campaign.branchRejoinPointsMap[this.args.selectedAction.id])
      ) {
        return exitMeta.branchId === this.reconnectForBranchId;
      }
    }

    return isSameBranch && isDifferentPath;
  }

  @action
  onMouseEnter() {
    if (this.canDrop && this.args.isSelectingDropAreaThroughClick) {
      this.onDragOver();
    }
  }

  @action
  onMouseLeave() {
    if (this.canDrop && this.args.isSelectingDropAreaThroughClick) {
      this.resetTranslatedNodes();
    }
  }

  @action
  onCanDropChange(canDrop: boolean) {
    this.canDrop = canDrop;
  }

  get isVisible() {
    return this.args.trackInViewport ? this.inViewport : true;
  }
  @tracked inViewport = false;

  @action
  onEnterViewport() {
    this.inViewport = true;
  }

  @action
  onExitViewport() {
    this.inViewport = false;
  }
}
